import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ClientIdDrop from "../components/ClientIdDrop";
import LatePaymentsDisputeModal from "../components/LatePaymentsDisputeModal";
import {
  Upload,
  FileText,
  Loader2,
  Sparkles,
  Trash2,
  Copy,
  Download,
  Plus,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  Send,
  X,
  Scale,
} from "lucide-react";
import DisputeLetterModal from "../components/DisputeLetterModal";
import { parseClientInfoForAsb } from "../utils/clientInfoParser";

const API = process.env.REACT_APP_BACKEND_URL;

// Sidebar-consistent category ordering from the spec.
// 2026-Feb: "Retail Card" merged into "Credit Card"; "Installment Loan"
// merged into "Personal Loan" — per user request.
// 2026-Feb (later): "Buy Now Pay Later" renamed to "Installment Loan"
// (own category again), and "Telecom" renamed to "Mobile".
const CATEGORY_ORDER = [
  "Credit Card",
  "Auto Loan",
  "Repossession",
  "Personal Loan",
  "Mortgage",
  "Apartment Lease",
  "Rental Lease",
  "Student Loan",
  "Installment Loan",
  "Timeshare",
  "Mobile",
  "Utility",
  "Medical",
  "Child Support",
  "Collection",
  "Other",
];

/** Any legacy/extractor labels that should collapse into another bucket. */
function normalizeAccountType(raw) {
  const s = String(raw || "").trim();
  if (s === "Retail Card") return "Credit Card";
  // Old label from previous LLM runs — remap so stored ASBs still display.
  if (s === "Buy Now Pay Later") return "Installment Loan";
  if (s === "Telecom") return "Mobile";
  return s || "Other";
}

const ACCEPTED_EXT = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif", ".pdf"];
const MAX_FILES = 20;

/** Format 1292 → "$1,292" (whole dollars) per the spec. null → "-" */
function fmtBalance(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

/** MM-DD-YYYY / MM-YYYY → sortable millis. Missing → +Infinity so nulls land last. */
function dateSortKey(iso) {
  if (!iso) return Number.MAX_SAFE_INTEGER;
  const parts = iso.split("-").map((n) => parseInt(n, 10));
  if (parts.length === 3) {
    const [m, d, y] = parts;
    return new Date(y, m - 1, d).getTime();
  }
  if (parts.length === 2) {
    const [m, y] = parts;
    return new Date(y, m - 1, 1).getTime();
  }
  return Number.MAX_SAFE_INTEGER;
}

/**
 * Build the plain-text output block per spec.
 * Only category headings + "NAME - DATE - BALANCE" lines + "Previous Creditor: X"
 * for collections. No extra columns, no status text.
 */
function buildOutputText(accounts) {
  const groups = new Map();
  accounts.forEach((a) => {
    const cat = a.accountType || "Other";
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat).push(a);
  });
  const sections = [];
  for (const cat of CATEGORY_ORDER) {
    const list = groups.get(cat);
    if (!list || !list.length) continue;
    // Sort: oldest → newest, missing dates last, tiebreak name then balance.
    list.sort((a, b) => {
      const dd = dateSortKey(a.openDate) - dateSortKey(b.openDate);
      if (dd !== 0) return dd;
      const nd = (a.accountName || "").localeCompare(b.accountName || "");
      if (nd !== 0) return nd;
      return (a.balance ?? Infinity) - (b.balance ?? Infinity);
    });
    const lines = [cat, ""];
    list.forEach((a) => {
      lines.push(
        `${a.accountName} - ${a.openDate || "-"} - ${fmtBalance(a.balance)}`,
      );
      if (cat === "Collection" && a.originalCreditor) {
        lines.push(`Previous Creditor: ${a.originalCreditor}`);
      }
      lines.push("");
    });
    sections.push(lines.join("\n").trim());
  }
  return sections.join("\n\n");
}

export default function AccountSummaryBreakdownPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // If we landed on ASB from the FTC Builder Step 1.5, remember that so
  // the "Send to FTC Report Builder" button can gracefully return there.
  const fromFtcBuilder = !!location.state?.fromFtcBuilder;

  // Hydrate client info once if we were passed some from the FTC Builder.
  useEffect(() => {
    const ci = location.state?.clientInfo;
    if (ci?.name) {
      setClientInfo({
        name: ci.name || "",
        address1: ci.address1 || "",
        address2: ci.address2 || "",
        phone: ci.phone || "",
        email: ci.email || "",
      });
      // Wipe router state so a refresh doesn't re-hydrate stale info.
      navigate(location.pathname, { replace: true, state: { fromFtcBuilder: !!location.state?.fromFtcBuilder } });
    }
  }, []);
  const [pastedText, setPastedText] = useState("");
  const [files, setFiles] = useState([]); // File[]
  const [extracting, setExtracting] = useState(false);
  const [accounts, setAccounts] = useState([]); // ExtractedAccount[]
  const [warnings, setWarnings] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  // Feb 2026 — user request: an "Inquiry Mode" toggle so the extractor
  // pulls HARD credit inquiries (creditor + full MM-DD-YYYY date, no
  // balance) instead of tradelines.  Feeds the new Inquiry Removal
  // dispute-letter template with the correctly-formatted inquiry rows.
  const [extractMode, setExtractMode] = useState("accounts"); // "accounts" | "inquiry" | "late_payments"
  // 2026-02-28 (user request) — When a late-payment extraction is selected
  // for letter-building, pre-fill the LatePaymentsDisputeModal with every
  // field the AI pulled (creditor name, open date, balance, months_late grid).
  const [lateLetterPrefill, setLateLetterPrefill] = useState(null);
  const openLateLetterFor = useCallback((acct) => {
    if (!acct) return;
    const marks = (acct.months_late || []).map((m) => ({
      month: String(m.month || "").padStart(2, "0"),
      year: Number(m.year),
      severity: Number(m.severity),
    })).filter((m) => m.month && m.year && m.severity);
    if (marks.length === 0) {
      toast.error("This account has no late marks to dispute.");
      return;
    }
    setLateLetterPrefill({
      creditor_name: (acct.accountName || "").trim(),
      creditor_address: "",
      creditor_city_state_zip: "",
      account_last4: "",
      // 2026-02-28 (user request) — carry the AI-extracted open date +
      // balance straight into the Late Payments modal's editable fields
      // so the admin's typing load is zero when the screenshot showed
      // the data.
      date_opened: acct.openDate || "",
      balance: acct.balance ?? null,
      marks,
      screenshot_base64: acct._screenshot || null,
    });
  }, []);
  // "Send to FTC Report Builder?" modal — shown right after a successful
  // extraction (Feb 2026 UX request).  Boolean is enough; the current
  // `accounts` array is what we forward.
  const [showFtcPrompt, setShowFtcPrompt] = useState(false);
  // Editable override for the copy preview at bottom of page — when
  // non-null, this text is what gets copied / sent to the FTC builder.
  const [previewOverride, setPreviewOverride] = useState(null);
  // Client Info (right on this page — carries through to Dispute Letter
  // and FTC handoff so the user doesn't retype it).
  const [clientInfo, setClientInfo] = useState({
    name: "",
    address1: "",
    address2: "",
    phone: "",
    email: "",
  });
  // Raw paste text for the client-info smart-paste box.
  const [clientInfoPaste, setClientInfoPaste] = useState("");
  // Dispute Letter modal — same UX as FTC Builder, drives Regular vs.
  // Second-Round + LLM-unique wording + typed/handwritten output.
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeClientName, setDisputeClientName] = useState("");
  const fileInputRef = useRef(null);

  /* ----- File picker helpers ----- */
  const addFiles = useCallback(
    (fList) => {
      const arr = Array.from(fList || []);
      if (!arr.length) return;
      // Filter by extension & MAX_FILES limit.
      const cleaned = [];
      for (const f of arr) {
        const dot = f.name.lastIndexOf(".");
        const ext = dot >= 0 ? f.name.slice(dot).toLowerCase() : "";
        if (!ACCEPTED_EXT.includes(ext)) {
          toast.error(`Skipped ${f.name}: unsupported type`);
          continue;
        }
        cleaned.push(f);
      }
      setFiles((prev) => {
        const merged = [...prev, ...cleaned];
        if (merged.length > MAX_FILES) {
          toast.error(`Max ${MAX_FILES} files — extras dropped`);
          return merged.slice(0, MAX_FILES);
        }
        return merged;
      });
    },
    [],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  /* ----- Main extraction call ----- */
  const runExtraction = async () => {
    if (!pastedText.trim() && files.length === 0) {
      toast.error("Paste text or attach at least one file first.");
      return;
    }
    setExtracting(true);
    setWarnings([]);
    try {
      const fd = new FormData();
      if (pastedText.trim()) fd.append("pastedText", pastedText);
      files.forEach((f) => fd.append("files", f));
      fd.append("mode", extractMode);

      // Two-step async pattern: kick off the extraction job, then poll
      // for completion.  This bypasses the ~60s Cloudflare / ingress
      // idle timeout that used to blow up large multi-PDF extracts with
      // a "520 empty response" error.
      const { data: startData } = await axios.post(
        `${API}/api/account-extract/start`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000, // upload only — the job runs server-side after this
        },
      );
      const jobId = startData?.jobId;
      if (!jobId) throw new Error("Extraction did not return a job id.");

      // Poll every 2s, up to 6 minutes.  Each GET is tiny so it never
      // trips the ingress timeout.  We surface the server-side `stage`
      // text so the user knows something is still happening.
      const startedAt = Date.now();
      const MAX_MS = 6 * 60 * 1000;
      let data = null;
      let lastStage = "Uploading…";
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        let pollData;
        try {
          const resp = await axios.get(
            `${API}/api/account-extract/status/${jobId}`,
            { timeout: 20000 },
          );
          pollData = resp.data;
        } catch (pollErr) {
          // Transient network hiccup during polling — don't abort the
          // whole extraction, just try again on the next tick.
          console.warn("[Extract] poll hiccup", pollErr?.message);
          if (Date.now() - startedAt > MAX_MS) {
            throw new Error("Lost connection while polling for extraction result.");
          }
          continue;
        }
        if (pollData.status === "done") {
          data = pollData;
          break;
        }
        if (pollData.status === "error") {
          throw new Error(pollData.error || "Extraction failed.");
        }
        // status === "running" — update the loading text if the server
        // gave us a fresher stage string.
        if (pollData.stage && pollData.stage !== lastStage) {
          lastStage = pollData.stage;
          toast.loading(lastStage, { id: "asb-extract-progress" });
        }
        if (Date.now() - startedAt > MAX_MS) {
          throw new Error("Extraction is taking too long. Try smaller / fewer files.");
        }
      }
      toast.dismiss("asb-extract-progress");

      // Ephemeral — REPLACE any previous list (no merging with prior runs
      // per the spec's session-isolation rule).
      setAccounts(
        (data.accounts || []).map((a) => ({
          ...a,
          accountType: normalizeAccountType(a.accountType),
        })),
      );
      setWarnings(data.warnings || []);
      toast.success(
        `Extracted ${data.count} account${data.count === 1 ? "" : "s"}. Review and edit below, then choose "Send to FTC Report Builder" when ready.`,
      );
      // Per user request Feb 2026: the "Create FTC Report?" prompt is
      // NOT auto-opened anymore.  The user reviews / edits the extracted
      // rows first, then explicitly clicks the toolbar button to
      // trigger the handoff modal.
    } catch (e) {
      console.error("[Extract]", e);
      const msg =
        e.code === "ECONNABORTED"
          ? "Extraction timed out. Try smaller / fewer files."
          : e.response?.data?.detail || e.message || "Unknown error";
      toast.error(`Extraction failed: ${msg}`);
    } finally {
      setExtracting(false);
    }
  };

  /* ----- Row edit helpers ----- */
  const updateAccount = (id, patch) => {
    setAccounts((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, ...patch, userEdited: true } : a,
      ),
    );
  };
  const deleteAccount = (id) => {
    setAccounts((prev) => prev.filter((a) => a.id !== id));
  };
  const addBlankAccount = (cat) => {
    setAccounts((prev) => [
      ...prev,
      {
        id: `manual-${Date.now()}`,
        accountName: "",
        openDate: null,
        openDatePrecision: "unknown",
        balance: null,
        accountType: cat,
        originalCreditor: null,
        confidence: {},
        userEdited: true,
        sourceIndex: prev.length,
      },
    ]);
  };
  const resetAll = () => {
    setAccounts([]);
    setWarnings([]);
    setFiles([]);
    setPastedText("");
  };

  /* ----- Copy / Download helpers ----- */
  const outputText = useMemo(() => buildOutputText(accounts), [accounts]);
  // Effective text used everywhere (copy / FTC handoff / txt download).
  // If the user has manually edited the preview textarea it wins; else
  // we regenerate live from the row edits.
  const effectiveOutput = previewOverride ?? outputText;

  const copyAll = async () => {
    if (!effectiveOutput) return toast.error("Nothing to copy.");
    try {
      await navigator.clipboard.writeText(effectiveOutput);
      toast.success("Account list copied.");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = effectiveOutput;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success("Account list copied.");
      else toast.error("Copy blocked — please select manually.");
    }
  };

  const copyCategory = async (cat) => {
    const only = accounts.filter((a) => a.accountType === cat);
    const txt = buildOutputText(only);
    if (!txt) return;
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`${cat} copied.`);
    } catch {
      toast.error("Copy blocked.");
    }
  };

  const download = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  const downloadTxt = () =>
    download(new Blob([effectiveOutput], { type: "text/plain" }), "accounts.txt");
  const downloadCsv = () => {
    const header = "category,accountName,openDate,balance,originalCreditor";
    const rows = accounts.map(
      (a) =>
        [
          a.accountType || "Other",
          `"${(a.accountName || "").replace(/"/g, '""')}"`,
          a.openDate || "",
          a.balance ?? "",
          a.originalCreditor
            ? `"${a.originalCreditor.replace(/"/g, '""')}"`
            : "",
        ].join(","),
    );
    download(
      new Blob([header + "\n" + rows.join("\n")], { type: "text/csv" }),
      "accounts.csv",
    );
  };
  const downloadJson = () =>
    download(
      new Blob([JSON.stringify(accounts, null, 2)], {
        type: "application/json",
      }),
      "accounts.json",
    );

  /* ----- FTC Report Builder handoff -----
   * Maps ASB's 16 categories → FTC Builder's 10 categories and formats
   * every row as "NAME - MM-DD-YYYY - $AMOUNT" (or MM-YYYY) — the exact
   * shape FTCBuilderPage.parseAccounts() expects.  Balance nulls become
   * "$0" so the row still lands with a name; the user can edit later.
   */
  const sendToFtcBuilder = () => {
    if (accounts.length === 0) return;
    // ASB category → FTC Builder category (10-cat schema)
    const CAT_MAP = {
      "Credit Card": "CREDIT_CARD",
      "Retail Card": "CREDIT_CARD",
      "Auto Loan": "AUTO",
      "Repossession": "AUTO",
      "Personal Loan": "PERSONAL_LOAN",
      "Installment Loan": "PERSONAL_LOAN",
      // Legacy label — old ASBs might still carry "Buy Now Pay Later".
      "Buy Now Pay Later": "PERSONAL_LOAN",
      "Mortgage": "MORTGAGE",
      // 2026-02-24 (user request) — Apartment/Rental lease first-class
      // categories on the ASB dropdown.  Backend already has
      // APARTMENT_LEASE / RENTAL_LEASE in CATEGORY_ORDER so the dispute
      // letter groups them correctly.
      "Apartment Lease": "APARTMENT_LEASE",
      "Rental Lease": "RENTAL_LEASE",
      "Student Loan": "STUDENT_LOAN",
      "Mobile": "UTILITY",
      // Legacy label — old ASBs might still carry "Telecom".
      "Telecom": "UTILITY",
      "Utility": "UTILITY",
      "Medical": "MEDICAL",
      "Child Support": "OTHER",
      "Timeshare": "OTHER",
      "Collection": "COLLECTION",
      "Other": "OTHER",
    };
    // Feb 2026 user request: Step 2 must show the accounts grouped
    // under their category header — identical to the ASB Copy Preview
    // format below.  We re-run the same grouping used by
    // buildOutputText() so a header line (e.g. "Auto Loan") sits above
    // every group of accounts.  FTCBuilderPage.parseAccounts() strips
    // these header lines during parsing (updated regex there).
    const groupsForText = new Map();
    const extractorAccounts = [];
    accounts.forEach((a) => {
      const name = (a.accountName || "").toUpperCase().trim();
      if (!name) return;
      const date = a.openDate || "";
      const bal = a.balance === null || a.balance === undefined
        ? 0
        : Math.round(Number(a.balance));
      const amt = `$${bal}`;
      const cat = a.accountType || "Other";
      if (!groupsForText.has(cat)) groupsForText.set(cat, []);
      groupsForText.get(cat).push({ name, date, amt, orig: a });
      extractorAccounts.push({
        account_name: name,
        date_opened: date,
        balance: bal,
        fraudAmount: amt,
        category: CAT_MAP[cat] || "OTHER",
      });
    });
    // Preserve the ASB category display order (same as CATEGORY_ORDER).
    const textLines = [];
    for (const cat of CATEGORY_ORDER) {
      const list = groupsForText.get(cat);
      if (!list || !list.length) continue;
      textLines.push(cat);        // category header — parser strips it
      textLines.push("");         // spacer line for readability
      list.forEach(({ name, date, amt, orig }) => {
        textLines.push([name, date, amt].filter(Boolean).join(" - "));
        if (cat === "Collection" && orig.originalCreditor) {
          textLines.push(`Previous Creditor: ${orig.originalCreditor}`);
        }
      });
      textLines.push("");         // blank line between category groups
    }
    if (extractorAccounts.length === 0) {
      toast.error("No usable accounts to send.");
      return;
    }
    setShowFtcPrompt(false);
    toast.success(`Sending ${extractorAccounts.length} accounts to FTC Builder…`);
    // Send both the manual fields AND, if the smart-paste box has content,
    // any city/state/zip we can split out — so FTC Step 1 lands fully filled.
    let clientPayload = null;
    if (clientInfo?.name) {
      let city = "", state = "", zip = "";
      if (clientInfo.address2) {
        const parts = clientInfo.address2.replace(/,/g, " ").replace(/\s+/g, " ").trim();
        const m = parts.match(/^(.*?)\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)?\s*$/i);
        if (m) {
          city = m[1] || "";
          state = (m[2] || "").toUpperCase();
          zip = m[3] || "";
        }
      }
      clientPayload = {
        name: clientInfo.name,
        address1: clientInfo.address1,
        address2: clientInfo.address2,
        city, state, zip,
        phone: clientInfo.phone,
        email: clientInfo.email,
      };
    }
    navigate("/ftc-builder", {
      state: {
        prefillFromAsb: {
          text: textLines.join("\n"),
          extractorAccounts,
          clientInfo: clientPayload,
        },
      },
    });
  };

  /* ----- Grouped view ----- */
  const grouped = useMemo(() => {
    const g = new Map();
    accounts.forEach((a) => {
      const cat = a.accountType || "Other";
      if (!g.has(cat)) g.set(cat, []);
      g.get(cat).push(a);
    });
    return CATEGORY_ORDER
      .filter((c) => g.has(c))
      .map((c) => [c, g.get(c)]);
  }, [accounts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-zinc-950 to-slate-950 text-slate-100 pt-16 pb-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-8" data-testid="asb-header">
          <div className="inline-flex items-center gap-2 bg-fuchsia-500/10 border border-fuchsia-400/30 text-fuchsia-200 text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Gemini-powered
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">
            Account Summary Breakdown
          </h1>
          <p className="text-slate-400 mt-2 max-w-2xl text-sm md:text-base">
            Paste a credit report or drop screenshots / PDFs. The extractor
            pulls only <span className="text-slate-200 font-medium">account
            name · open date · balance</span> — nothing else. Every run
            starts fresh, no data is saved on our servers.
          </p>
        </div>

        {/* Input card */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 md:p-6 mb-6">
          <label className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
            Paste credit report text
          </label>
          <textarea
            data-testid="asb-paste-textarea"
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Paste the full report — Credit Karma, Experian, Equifax, TransUnion, mortgage credit reports, OCR text, or plain lists…"
            rows={7}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-fuchsia-400/60 focus:outline-none resize-y font-mono"
          />

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">
              or attach files
            </span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* File dropzone */}
          <div
            data-testid="asb-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed transition-colors p-6 text-center ${
              dragActive
                ? "border-fuchsia-400 bg-fuchsia-500/5"
                : "border-white/10 hover:border-fuchsia-400/50 hover:bg-white/[0.02]"
            }`}
          >
            <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <div className="text-sm text-slate-300">
              Drop screenshots or PDFs here, or{" "}
              <span className="text-fuchsia-300 underline underline-offset-2">
                click to browse
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              JPG · PNG · WebP · HEIC · PDF · up to {MAX_FILES} files, 60 MB each
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXT.join(",")}
              multiple
              hidden
              onChange={(e) => addFiles(e.target.files)}
              data-testid="asb-file-input"
            />
          </div>

          {/* Attached files list */}
          {files.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="asb-file-list">
              {files.map((f, i) => (
                <div
                  key={i}
                  className="inline-flex items-center gap-2 bg-white/[0.05] border border-white/10 rounded-lg px-2.5 py-1 text-xs"
                >
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span className="max-w-[180px] truncate">{f.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFiles((prev) => prev.filter((_, j) => j !== i));
                    }}
                    className="text-slate-400 hover:text-rose-400"
                    aria-label={`Remove ${f.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <ClientIdDrop clientName={clientInfo.name} />

          {/* Actions */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {/* Extract-mode toggle — chooses between tradeline extraction
                and hard-inquiry extraction (used for the Inquiry Removal
                dispute letter, which needs FULL MM-DD-YYYY dates). */}
            <div
              className="inline-flex items-center bg-black/40 border border-white/10 rounded-xl p-1 text-xs"
              data-testid="asb-extract-mode-toggle"
              role="tablist"
              aria-label="Extraction mode"
            >
              <button
                type="button"
                onClick={() => setExtractMode("accounts")}
                data-testid="asb-mode-accounts"
                aria-pressed={extractMode === "accounts"}
                className={`px-3 py-1.5 rounded-lg font-semibold transition-colors ${
                  extractMode === "accounts"
                    ? "bg-fuchsia-600 text-white shadow-md"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                Accounts
              </button>
              <button
                type="button"
                onClick={() => setExtractMode("inquiry")}
                data-testid="asb-mode-inquiry"
                aria-pressed={extractMode === "inquiry"}
                className={`px-3 py-1.5 rounded-lg font-semibold transition-colors ${
                  extractMode === "inquiry"
                    ? "bg-amber-600 text-white shadow-md"
                    : "text-slate-400 hover:text-white"
                }`}
                title="Extract hard credit inquiries with the full MM-DD-YYYY date"
              >
                Inquiries
              </button>
              <button
                type="button"
                onClick={() => setExtractMode("late_payments")}
                data-testid="asb-mode-late-payments"
                aria-pressed={extractMode === "late_payments"}
                className={`px-3 py-1.5 rounded-lg font-semibold transition-colors ${
                  extractMode === "late_payments"
                    ? "bg-rose-600 text-white shadow-md"
                    : "text-slate-400 hover:text-white"
                }`}
                title="Extract tradelines with late-payment marks (30/60/90/120/150/180 days)"
              >
                Late Payments
              </button>
            </div>

            <button
              data-testid="asb-extract-btn"
              onClick={runExtraction}
              disabled={extracting || (!pastedText.trim() && !files.length)}
              className="inline-flex items-center gap-2 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-xl text-sm shadow-lg shadow-fuchsia-900/40 transition-colors"
            >
              {extracting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Extracting…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {extractMode === "inquiry"
                    ? "Extract Inquiries"
                    : extractMode === "late_payments"
                    ? "Extract Late Payments"
                    : "Extract Accounts"}
                </>
              )}
            </button>
            {(accounts.length > 0 || pastedText || files.length > 0) && (
              <button
                data-testid="asb-reset-btn"
                onClick={resetAll}
                disabled={extracting}
                className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-white/[0.06] transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> New Extraction
              </button>
            )}
          </div>
        </div>

        {/* Warnings from Gemini */}
        {warnings.length > 0 && (
          <div className="mb-6 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-100 p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold mb-1">Notes from extractor</div>
                <ul className="list-disc list-inside space-y-0.5 text-amber-200/80">
                  {warnings.map((w, i) => (
                    <li key={i}>{typeof w === "string" ? w : JSON.stringify(w)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {accounts.length > 0 && (
          <>
            {/* Toolbar */}
            <div className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 py-3 mb-4 bg-slate-950/85 backdrop-blur border-b border-white/10 flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                {accounts.length} account{accounts.length === 1 ? "" : "s"}
              </div>
              <div className="grow" />
              <button
                data-testid="asb-copy-all-btn"
                onClick={copyAll}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-3.5 py-2 rounded-lg shadow-md shadow-emerald-900/40"
              >
                <Copy className="w-4 h-4" /> Copy All Accounts
              </button>
              {/* 2026-Feb: 'Send to FTC Report Builder' button removed —
                  ASB is now an extract/copy tool only. The FTC Report Builder
                  is the single place client info + accounts + dispute letters
                  live end-to-end. The Return-to-FTC-Builder button still shows
                  when the user arrived via the FTC Step 1½ button.  */}
              {fromFtcBuilder && (
                <button
                  data-testid="asb-send-to-ftc-btn"
                  onClick={sendToFtcBuilder}
                  className="inline-flex items-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-semibold px-3.5 py-2 rounded-lg shadow-md shadow-fuchsia-900/40"
                  title="Return the extracted accounts to the FTC Report Builder"
                >
                  <Send className="w-4 h-4" /> Return to FTC Builder →
                </button>
              )}
              <button
                onClick={downloadTxt}
                className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.10] text-slate-100 text-sm px-3 py-2 rounded-lg"
                data-testid="asb-download-txt"
              >
                <Download className="w-3.5 h-3.5" /> .txt
              </button>
              <button
                onClick={downloadCsv}
                className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.10] text-slate-100 text-sm px-3 py-2 rounded-lg"
                data-testid="asb-download-csv"
              >
                <Download className="w-3.5 h-3.5" /> .csv
              </button>
              <button
                onClick={downloadJson}
                className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.10] text-slate-100 text-sm px-3 py-2 rounded-lg"
                data-testid="asb-download-json"
              >
                <Download className="w-3.5 h-3.5" /> .json
              </button>
            </div>

            {/* 2026-Feb: Client Info section removed. FTC Report Builder
                collects client info separately; ASB stays focused on
                account extraction. */}

            {/* Category groups */}
            <div className="space-y-6">
              {grouped.map(([cat, list]) => (
                <CategoryBlock
                  key={cat}
                  category={cat}
                  accounts={list}
                  extractMode={extractMode}
                  onUpdate={updateAccount}
                  onDelete={deleteAccount}
                  onCopy={() => copyCategory(cat)}
                  onAddBlank={() => addBlankAccount(cat)}
                  onBuildLetter={openLateLetterFor}
                />
              ))}
            </div>

            {/* Preview of copy output — editable so you can hand-tune
                the exact text that lands on clipboard / gets passed to
                the FTC builder + dispute letter. */}
            <div className="mt-8 bg-black/40 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Copy preview (editable — this is what lands on your clipboard)
                </div>
                <button
                  onClick={() => setPreviewOverride(null)}
                  disabled={previewOverride === null}
                  className="text-[11px] text-slate-400 hover:text-fuchsia-300 disabled:opacity-40 disabled:hover:text-slate-400"
                  data-testid="asb-preview-reset-btn"
                >
                  Reset to auto
                </button>
              </div>
              <textarea
                data-testid="asb-preview-output"
                value={previewOverride ?? outputText}
                onChange={(e) => setPreviewOverride(e.target.value)}
                rows={Math.min(24, Math.max(6, (previewOverride ?? outputText).split("\n").length + 2))}
                className="w-full bg-black/30 border border-white/5 rounded-lg px-3 py-2 whitespace-pre-wrap text-sm text-slate-200 font-mono leading-relaxed focus:border-fuchsia-400/50 focus:outline-none"
              />
              {previewOverride !== null && (
                <div className="mt-2 text-[11px] text-amber-300">
                  ⚠ You&apos;ve manually edited this text — it will be used exactly as shown for Copy All / Send to FTC Builder.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ------------------------------------------------------------------
          "Create an FTC Report?" modal — offered right after a successful
          extraction.  Yes → pre-populates the FTC Builder's Step 2 (Paste
          Accounts) with these accounts already formatted, so the user
          can jump straight to parsing + building.
      ------------------------------------------------------------------ */}
      {showFtcPrompt && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowFtcPrompt(false)}
          data-testid="asb-ftc-prompt-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-900 to-zinc-900 border border-fuchsia-500/30 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-fuchsia-900/40"
            data-testid="asb-ftc-prompt-modal"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-11 h-11 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-fuchsia-300" />
              </div>
              <button
                onClick={() => setShowFtcPrompt(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/[0.06]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <h3 className="text-lg font-bold text-white mb-1.5">
              Create an FTC Report?
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Ready to send <span className="font-semibold text-fuchsia-200">
              {accounts.length} account{accounts.length === 1 ? "" : "s"}</span>{" "}
              to the FTC Report Builder. You&apos;ll be taken to the
              <em> Client Info</em> step first — once you fill that in and
              click Next, these accounts will already be pasted and parsed
              on the <em>Paste Accounts</em> screen. Then it&apos;s
              business as usual.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFtcPrompt(false)}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.10] text-slate-200 text-sm font-medium py-2.5 rounded-xl transition-colors"
                data-testid="asb-ftc-prompt-no"
              >
                No, stay here
              </button>
              <button
                onClick={sendToFtcBuilder}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white text-sm font-semibold py-2.5 rounded-xl shadow-md shadow-fuchsia-900/40 transition-colors"
                data-testid="asb-ftc-prompt-yes"
              >
                <Send className="w-4 h-4" /> Yes, build report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2026-Feb: Dispute Letter modal removed from ASB. Dispute letters
          now flow ONLY through the FTC Report Builder → post-generation
          prompt, so ASB stays focused on account extraction. */}

      {/* 2026-02-28 (user request) — Late-payment dispute letter modal
          driven by the AI extractor.  Opens when a user clicks "Build
          Letter" on any extracted late-payment row.  Every section
          (creditor name, late marks grid) is pre-filled from the AI
          extraction. */}
      <LatePaymentsDisputeModal
        key={lateLetterPrefill?.creditor_name || "idle"}
        open={!!lateLetterPrefill}
        onClose={() => setLateLetterPrefill(null)}
        clientData={
          clientInfo?.name
            ? {
                name: clientInfo.name,
                address: clientInfo.address1 || "",
                city: "",
                state: "",
                zip_code: "",
              }
            : {}
        }
        prefill={lateLetterPrefill}
        onGenerated={() => {
          toast.success("Late-payment dispute letter downloaded");
          setLateLetterPrefill(null);
        }}
      />
    </div>
  );
}

/* --------------------------------------------------------------------------
   CategoryBlock — one heading + inline-editable rows for a single category.
   Kept small on purpose; per-row editing state lives on the parent so the
   Copy Preview stays perfectly in sync.
-------------------------------------------------------------------------- */
function CategoryBlock({ category, accounts, extractMode, onUpdate, onDelete, onCopy, onAddBlank, onBuildLetter }) {
  const isCollection = category === "Collection";
  return (
    <section
      className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden"
      data-testid={`asb-category-${category.replace(/\s+/g, "-")}`}
    >
      <header className="flex items-center gap-2 px-4 md:px-5 py-3 bg-white/[0.04] border-b border-white/10">
        <h2 className="font-bold text-slate-100">{category}</h2>
        <span className="text-xs text-slate-400">
          {accounts.length} account{accounts.length === 1 ? "" : "s"}
        </span>
        <div className="grow" />
        <button
          onClick={onCopy}
          className="text-xs inline-flex items-center gap-1 text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-white/[0.06]"
          data-testid={`asb-copy-cat-${category.replace(/\s+/g, "-")}`}
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
        <button
          onClick={onAddBlank}
          className="text-xs inline-flex items-center gap-1 text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-white/[0.06]"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </header>
      <div className="divide-y divide-white/5">
        {accounts.map((a) => (
          <AccountRow
            key={a.id}
            account={a}
            showCollectionField={isCollection}
            extractMode={extractMode}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onBuildLetter={onBuildLetter}
          />
        ))}
      </div>
    </section>
  );
}

function AccountRow({ account, showCollectionField, extractMode, onUpdate, onDelete, onBuildLetter }) {
  const [balanceRaw, setBalanceRaw] = useState(
    account.balance === null || account.balance === undefined
      ? ""
      : String(account.balance),
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.9fr_1fr_auto] gap-2 md:gap-3 items-center px-4 md:px-5 py-3">
      {/* Name */}
      <input
        type="text"
        value={account.accountName || ""}
        onChange={(e) => onUpdate(account.id, { accountName: e.target.value })}
        placeholder="Creditor / account name"
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-slate-100 focus:border-fuchsia-400/60 focus:outline-none"
        data-testid={`asb-row-name-${account.id}`}
      />
      {/* Open date */}
      <input
        type="text"
        value={account.openDate || ""}
        onChange={(e) =>
          onUpdate(account.id, { openDate: e.target.value || null })
        }
        placeholder="MM-DD-YYYY"
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-slate-100 focus:border-fuchsia-400/60 focus:outline-none font-mono"
        data-testid={`asb-row-date-${account.id}`}
      />
      {/* Balance */}
      <input
        type="text"
        inputMode="decimal"
        value={balanceRaw}
        onChange={(e) => {
          const raw = e.target.value;
          setBalanceRaw(raw);
          if (raw.trim() === "") {
            onUpdate(account.id, { balance: null });
          } else {
            const n = parseFloat(raw.replace(/[^0-9.-]/g, ""));
            onUpdate(account.id, { balance: Number.isFinite(n) ? n : null });
          }
        }}
        placeholder="Balance (blank = —)"
        className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-slate-100 focus:border-fuchsia-400/60 focus:outline-none font-mono"
        data-testid={`asb-row-balance-${account.id}`}
      />
      {/* Move-to-Category dropdown — Feb 2026 addition.  Client-repair
          consultants frequently need to reclassify (e.g., Gemini tagged
          a charged-off credit card as a Collection but the row still
          reports to Credit Card).  Selecting a new value moves the row
          to the target category group automatically via the parent
          `grouped` memo. */}
      <select
        value={account.accountType || "Other"}
        onChange={(e) => onUpdate(account.id, { accountType: e.target.value })}
        className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-100 focus:border-fuchsia-400/60 focus:outline-none cursor-pointer"
        data-testid={`asb-row-category-${account.id}`}
        title="Move this account to a different category"
      >
        {CATEGORY_ORDER.map((c) => (
          <option key={c} value={c} className="bg-slate-900">
            {c}
          </option>
        ))}
      </select>
      {/* Delete */}
      <button
        onClick={() => onDelete(account.id)}
        className="justify-self-start md:justify-self-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg p-1.5 transition-colors"
        aria-label={`Delete ${account.accountName}`}
        data-testid={`asb-row-delete-${account.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
      {/* 2026-02-28 (user request) — late-payment row shows a chip
          strip of every extracted month+severity AND a "Build Letter"
          button that opens the LatePaymentsDisputeModal with every
          section pre-filled from this account's AI extraction. */}
      {extractMode === "late_payments" && Array.isArray(account.months_late) && account.months_late.length > 0 && (
        <div className="md:col-span-5 mt-2 flex items-center flex-wrap gap-2 border-t border-white/10 pt-2">
          <span className="text-[10px] uppercase tracking-wider text-rose-300/80 font-semibold mr-1">
            Months late:
          </span>
          {account.months_late.map((m, idx) => {
            const sev = Number(m.severity);
            const col =
              sev >= 150 ? "bg-rose-500/20 border-rose-400/50 text-rose-200"
              : sev >= 90 ? "bg-orange-500/20 border-orange-400/50 text-orange-200"
              : sev >= 60 ? "bg-amber-500/20 border-amber-400/50 text-amber-200"
              : "bg-yellow-500/15 border-yellow-400/40 text-yellow-200";
            return (
              <span key={idx}
                    className={`text-[11px] font-mono border rounded px-2 py-0.5 ${col}`}
                    data-testid={`asb-row-latemark-${account.id}-${idx}`}>
                {String(m.month).padStart(2,"0")}/{m.year} · {sev}d
              </span>
            );
          })}
          <div className="grow" />
          <button
            onClick={() => onBuildLetter && onBuildLetter(account)}
            className="text-[11px] font-semibold inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-500 text-white rounded px-2.5 py-1 shadow-sm"
            data-testid={`asb-row-build-letter-${account.id}`}
          >
            <FileText className="w-3 h-3" />
            Build Letter
          </button>
        </div>
      )}
      {/* Original creditor for collections */}
      {showCollectionField && (
        <div className="md:col-span-5 mt-1">
          <input
            type="text"
            value={account.originalCreditor || ""}
            onChange={(e) =>
              onUpdate(account.id, {
                originalCreditor: e.target.value || null,
              })
            }
            placeholder="Previous / original creditor (e.g. CAPITAL ONE PLATINUM)"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-slate-100 focus:border-fuchsia-400/60 focus:outline-none italic"
            data-testid={`asb-row-orig-creditor-${account.id}`}
          />
        </div>
      )}
    </div>
  );
}
